/**
 * Der Chronos-Shop (GDD §2) — der Echtgeld-Einstiegspunkt.
 *
 * GDD-Zitat (Progress-Balance & Fair-Play-Grenzen): "Chronos-Kristall-
 * Deckelung: Die käufliche Menge an Chronos-Kristallen ist pro Tag fest
 * begrenzt." Diese Grenze ist hier real implementiert, obwohl noch kein
 * Zahlungsanbieter angebunden ist (docs/TODO.md) — jeder Kauf wird sofort
 * gutgeschrieben, als Platzhalter für den künftigen Checkout. Die Regel
 * jetzt schon korrekt zu haben (statt sie nachträglich anzuflicken) ist
 * billiger, als sie erst beim echten Zahlungsanbieter nachzurüsten.
 *
 * Die Rohstoff-Pakete und gemischten Bundles sind eine bewusste Erweiterung
 * über das GDD hinaus (Owner-Wunsch, nicht GDD-vorgeschrieben) — das GDD
 * kennt nur Chronos-Kristalle als käufliche Währung. Rohstoffmengen skalieren
 * daher an der AKTUELLEN Förderrate des Spielers (`productionReport`) statt
 * an einer festen Zahl, damit ein Paket bei Level 1 wie bei Level 80
 * gleichermaßen sinnvoll bleibt — der Preis bleibt fest, die Menge nicht.
 */

import type { GameState, ResourceAmounts } from './types.js';
import { productionReport } from './state.js';

/** One 24 h purchasable-Chronos ceiling, independent of account level — real
 * money doesn't scale with level the way gameplay currencies do. Design
 * fill-in: sized so a single large pack fits, but not two, per day. */
export const CHRONOS_DAILY_CAP = 6_000;

export function createShop(now: number): import('./types.js').ShopState {
  return {
    chronosBoughtToday: 0,
    dayStartedAt: now,
    totalChronosBought: 0,
    totalSpentEur: 0,
    redeemedOnce: [],
  };
}

/** Roll the 24 h window forward if it has elapsed. Mutates. */
export function rolloverShopDay(state: GameState, now: number): void {
  const elapsed = now - state.shop.dayStartedAt;
  if (elapsed >= 86_400_000) {
    const days = Math.floor(elapsed / 86_400_000);
    state.shop.dayStartedAt += days * 86_400_000;
    state.shop.chronosBoughtToday = 0;
  }
}

/** Chronos-Kristalle still purchasable today. */
export function remainingDailyChronos(state: GameState, now: number): number {
  const elapsed = now - state.shop.dayStartedAt;
  const bought = elapsed >= 86_400_000 ? 0 : state.shop.chronosBoughtToday;
  return Math.max(0, CHRONOS_DAILY_CAP - bought);
}

export type ShopCategory = 'chronos' | 'resources' | 'bundle';

export interface ShopProduct {
  id: string;
  category: ShopCategory;
  label: string;
  /** One sentence — what you get, in plain terms. */
  description: string;
  /** Symbolic price. No payment provider is wired up yet. */
  priceEur: number;
  /** Fixed Chronos-Kristalle grant, if any (packs and the Chronos half of bundles). */
  chronos?: number;
  /**
   * Hours of the player's CURRENT production (titan/silicate/isotope)
   * granted, resolved at purchase time via `productGrant` — never a fixed
   * number, so the pack stays proportionally useful at any level.
   */
  resourceHours?: number;
  /** Marketing badge shown on the card, e.g. "Beliebt", "Bester Wert". */
  badge?: string;
  /** Shown once; disappears from the catalogue after being redeemed. */
  oneTime?: boolean;
  /** Sprite ids (packages/assets) composed into the card art, 1–3 icons. */
  art: string[];
}

/**
 * Six Chronos-Kristall-Pakete mit wachsendem Kristalle-pro-Euro-Verhältnis —
 * die "Bonus"-Prozente, die die UI anzeigt, werden aus genau diesem
 * Verhältnis berechnet (siehe `chronosPerEuro`/`chronosBonusPercent` unten),
 * nicht separat gepflegt.
 */
const CHRONOS_PACKS: ShopProduct[] = [
  {
    id: 'chronos_starter',
    category: 'chronos',
    label: 'Starterpaket',
    description: '60 Chronos-Kristalle',
    priceEur: 0.99,
    chronos: 60,
    art: ['res-chronos'],
  },
  {
    id: 'chronos_small',
    category: 'chronos',
    label: 'Kleines Kristallpaket',
    description: '160 Chronos-Kristalle',
    priceEur: 2.49,
    chronos: 160,
    art: ['res-chronos'],
  },
  {
    id: 'chronos_medium',
    category: 'chronos',
    label: 'Mittleres Kristallpaket',
    description: '450 Chronos-Kristalle',
    priceEur: 6.49,
    chronos: 450,
    art: ['res-chronos'],
  },
  {
    id: 'chronos_large',
    category: 'chronos',
    label: 'Großes Kristallpaket',
    description: '1.000 Chronos-Kristalle',
    priceEur: 13.49,
    chronos: 1_000,
    badge: 'Beliebt',
    art: ['res-chronos'],
  },
  {
    id: 'chronos_mega',
    category: 'chronos',
    label: 'Mega-Kristallpaket',
    description: '2.200 Chronos-Kristalle',
    priceEur: 26.99,
    chronos: 2_200,
    badge: 'Bester Wert',
    art: ['res-chronos'],
  },
  {
    id: 'chronos_commander',
    category: 'chronos',
    label: 'Flotten-Kommandeur-Paket',
    description: '5.000 Chronos-Kristalle',
    priceEur: 55.99,
    chronos: 5_000,
    badge: 'Maximaler Bonus',
    art: ['res-chronos'],
  },
];

/** Rohstoff-Pakete — Menge skaliert an der aktuellen Förderrate, siehe Modul-Kommentar. */
const RESOURCE_PACKS: ShopProduct[] = [
  {
    id: 'resources_small',
    category: 'resources',
    label: 'Vorrats-Nachschub',
    description: '≈ 3 Stunden Förderung, sofort geliefert',
    priceEur: 1.99,
    resourceHours: 3,
    art: ['res-titan', 'res-silicate', 'res-isotope'],
  },
  {
    id: 'resources_medium',
    category: 'resources',
    label: 'Rohstoff-Konvoi',
    description: '≈ 12 Stunden Förderung, sofort geliefert',
    priceEur: 4.99,
    resourceHours: 12,
    art: ['res-titan', 'res-silicate', 'res-isotope'],
  },
  {
    id: 'resources_large',
    category: 'resources',
    label: 'Frachterladung',
    description: '≈ 2 Tage Förderung, sofort geliefert',
    priceEur: 9.99,
    resourceHours: 48,
    badge: 'Bester Wert',
    art: ['res-titan', 'res-silicate', 'res-isotope'],
  },
];

/** Gemischte Bundles — Chronos + Rohstoffe, bewusst als bester €-Wert positioniert. */
const BUNDLES: ShopProduct[] = [
  {
    id: 'bundle_starter',
    category: 'bundle',
    label: 'Kommandanten-Startpaket',
    description: '200 Chronos + ≈ 12 Stunden Förderung — nur einmal verfügbar',
    priceEur: 4.99,
    chronos: 200,
    resourceHours: 12,
    oneTime: true,
    badge: 'Einmaliges Angebot',
    art: ['res-chronos', 'res-titan'],
  },
  {
    id: 'bundle_builder',
    category: 'bundle',
    label: 'Bauherren-Paket',
    description: '150 Chronos + ≈ 24 Stunden Förderung',
    priceEur: 9.99,
    chronos: 150,
    resourceHours: 24,
    art: ['res-chronos', 'res-silicate'],
  },
  {
    id: 'bundle_warchest',
    category: 'bundle',
    label: 'Kriegskasse',
    description: '600 Chronos + ≈ 2 Tage Förderung',
    priceEur: 19.99,
    chronos: 600,
    resourceHours: 48,
    badge: 'Bester Wert',
    art: ['res-chronos', 'res-titan', 'res-isotope'],
  },
];

export const SHOP_PRODUCTS: Record<string, ShopProduct> = Object.fromEntries(
  [...CHRONOS_PACKS, ...RESOURCE_PACKS, ...BUNDLES].map((p) => [p.id, p]),
);
export const SHOP_PRODUCT_IDS = Object.keys(SHOP_PRODUCTS);
export const SHOP_CATEGORY_PRODUCTS: Record<ShopCategory, ShopProduct[]> = {
  chronos: CHRONOS_PACKS,
  resources: RESOURCE_PACKS,
  bundle: BUNDLES,
};

/** Chronos-Kristalle pro Euro — die Basis für den angezeigten Mengenrabatt. */
export function chronosPerEuro(product: ShopProduct): number {
  return (product.chronos ?? 0) / product.priceEur;
}

/** Mengenrabatt in Prozent gegenüber dem kleinsten Chronos-Paket. */
export function chronosBonusPercent(product: ShopProduct): number {
  const base = CHRONOS_PACKS[0]!;
  const rate = chronosPerEuro(product);
  const baseRate = chronosPerEuro(base);
  return Math.round((rate / baseRate - 1) * 100);
}

/** Rohstoff-Anteil eines Pakets, aus der aktuellen Förderrate berechnet — nie fest. */
const ISOTOPE_FLOOR_PER_HOUR = 13; // ~150 Isotop nach 12h auch ohne Kondensator, design fill-in

function resourceGrant(state: GameState, now: number, hours: number): ResourceAmounts {
  const report = productionReport(state, now);
  return {
    titan: Math.round(report.perHour.titan * hours),
    silicate: Math.round(report.perHour.silicate * hours),
    isotope: Math.round(Math.max(report.perHour.isotope, ISOTOPE_FLOOR_PER_HOUR) * hours),
  };
}

/** Full grant for a product, resolved against the CURRENT state — the one source of truth for what a purchase pays out. */
export function productGrant(state: GameState, now: number, product: ShopProduct): ResourceAmounts {
  const grant: ResourceAmounts =
    product.resourceHours !== undefined ? resourceGrant(state, now, product.resourceHours) : {};
  if (product.chronos) grant.chronos = (grant.chronos ?? 0) + product.chronos;
  return grant;
}
